import { useState } from 'react';

import { AutoFixHigh } from '@mui/icons-material';

import {
    setPreHeader,
    setSenderProfileId,
    setSubjectLine,
} from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import SubjectLinesDialog from 'domains/campaigns/components/SubjectLinesDialog';
import { EmailSendInputState } from 'domains/campaigns/types';
import { SenderProfile } from 'domains/content/types';
import IconButton from 'domains/core/components/IconButton';
import InputLabel from 'domains/core/components/InputLabel';
import Select from 'domains/core/components/Select';
import TextField from 'domains/core/components/TextField';
import useSenderProfiles from 'hooks/queries/useSenderProfiles';
import useTemplateSuggestedSubjectLines from 'hooks/queries/useTemplateSuggestedSubjectLines';

type Props = {
    activeSendIndex: number;
    currentSend: EmailSendInputState;
    dispatchCampaignAction: React.Dispatch<any>;
    isSharedMode?: boolean; // If true, changes apply to both sends (for send time A/B testing)
    isABTestingContent?: boolean; // If true, we're in A/B content testing mode
    campaignSends?: any[]; // Array of campaign sends to check Email-B state
};

const AdditionalContentFields = ({
    activeSendIndex,
    currentSend,
    dispatchCampaignAction,
    isSharedMode = false,
    isABTestingContent = false,
    campaignSends = [],
}: Props) => {
    const { preHeader, senderProfileId, subjectLine, templateId } = currentSend;
    const [isSubjectLinesDialogOpen, setIsSubjectLinesDialogOpen] = useState(false);

    const { isLoading: isSenderProfilesLoading, data: senderProfiles } = useSenderProfiles();

    const { data: suggestedSubjectLines } = useTemplateSuggestedSubjectLines({
        templateId,
        reactQueryOptions: {
            enabled: !!templateId,
        },
    });

    const subjectLineGeneratorHelperText = templateId
        ? `Click to use Cured's Subject Line Curator.`
        : `Select content for the Subject Line Generator.`;

    // Helper function to handle auto-copy logic for A/B content testing
    const handleAutoCopyToEmailB = (
        fieldKey: keyof EmailSendInputState,
        value: any,
        actionCreator: (value: any, sendIndex: number) => any
    ) => {
        // Auto-copy feature: If A/B content testing and updating Email-A, copy to Email-B
        // Only copy if user is currently editing Email-A and Email-B hasn't been manually modified
        if (isABTestingContent && activeSendIndex === 0 && campaignSends[1]) {
            const emailA = campaignSends[0];
            const emailB = campaignSends[1];

            // Check if Email-B appears to be auto-copied (matches Email-A's current value or is empty)
            // This heuristic determines if Email-B has been manually modified
            const emailBLooksAutoCopied = !emailB[fieldKey] || emailB[fieldKey] === emailA[fieldKey];

            if (emailBLooksAutoCopied) {
                dispatchCampaignAction(actionCreator(value, 1));
            }
        }
    };

    // Helper functions to handle shared mode dispatching
    const handleSenderProfileChange = (value: any) => {
        if (isSharedMode) {
            // Apply to both sends
            dispatchCampaignAction(setSenderProfileId(value, 0));
            dispatchCampaignAction(setSenderProfileId(value, 1));
        } else {
            dispatchCampaignAction(setSenderProfileId(value, activeSendIndex));
            handleAutoCopyToEmailB('senderProfileId', value, setSenderProfileId);
        }
    };

    const handleSubjectLineChange = (value: string) => {
        if (isSharedMode) {
            // Apply to both sends
            dispatchCampaignAction(setSubjectLine(value, 0));
            dispatchCampaignAction(setSubjectLine(value, 1));
        } else {
            dispatchCampaignAction(setSubjectLine(value, activeSendIndex));
            handleAutoCopyToEmailB('subjectLine', value, setSubjectLine);
        }
    };

    const handlePreHeaderChange = (value: string) => {
        if (isSharedMode) {
            // Apply to both sends
            dispatchCampaignAction(setPreHeader(value, 0));
            dispatchCampaignAction(setPreHeader(value, 1));
        } else {
            dispatchCampaignAction(setPreHeader(value, activeSendIndex));
            handleAutoCopyToEmailB('preHeader', value, setPreHeader);
        }
    };

    return (
        <>
            <Select
                data-testid="email-sender-profile-select"
                required
                label={<InputLabel label="Sender profile" labelIconTooltipText="Who is sending this email?" />}
                menuItemStyles={{ textTransform: 'none' }}
                onChange={(e: any) => handleSenderProfileChange(e.target.value)}
                value={senderProfileId ?? ''}
                values={
                    !isSenderProfilesLoading
                        ? senderProfiles?.map((senderProfile: SenderProfile) => ({
                              value: senderProfile.id,
                              label: senderProfile.from_address,
                          }))
                        : []
                }
                fullWidth
            />
            <div style={{ position: 'relative' }}>
                <TextField
                    counterCurrent={subjectLine?.length ?? 0}
                    counterMax={255}
                    data-test="curation-content-page-subject-line"
                    data-testid="email-subject-input"
                    fullWidth
                    inputProps={{ maxLength: 255 }}
                    label={
                        <InputLabel
                            label="Subject line"
                            labelIconTooltipText="A descriptive overview of your message that gives contacts a reason to open."
                        />
                    }
                    onChange={(e: any) => handleSubjectLineChange(e.target.value)}
                    value={subjectLine}
                    withCounter
                />
                <IconButton
                    buttonWrapperCss={{
                        position: 'absolute',
                        right: '-50px',
                        top: '4px',
                    }}
                    disabled={!templateId}
                    handleClick={() => setIsSubjectLinesDialogOpen(true)}
                    Icon={AutoFixHigh}
                    title={subjectLineGeneratorHelperText}
                />
                <SubjectLinesDialog
                    isOpen={isSubjectLinesDialogOpen}
                    subjectLines={suggestedSubjectLines}
                    onClickSubjectLine={(index: number) => {
                        handleSubjectLineChange(suggestedSubjectLines[index]);
                    }}
                    onClose={() => setIsSubjectLinesDialogOpen(false)}
                />
            </div>
            <TextField
                counterCurrent={preHeader?.length ?? 0}
                counterMax={100}
                data-test="curation-content-page-preview"
                data-testid="email-preview-text-input"
                fullWidth
                inputProps={{ maxLength: 100 }}
                label={
                    <InputLabel
                        label="Preview text"
                        labelIconTooltipText="A quick glimpse into the content of your message that appears after the subject line."
                    />
                }
                onChange={(e: any) => handlePreHeaderChange(e.target.value)}
                value={preHeader}
                withCounter
            />
        </>
    );
};

export default AdditionalContentFields;
